import React, { useEffect, useRef, useState } from 'react';
import { Bold, Heading2, List, Link as LinkIcon, Image as ImageIcon, Loader2 } from 'lucide-react';

interface RichTextEditorProps {
  /** 現在の HTML 本文 */
  value: string;
  /** 本文 HTML が変わったとき */
  onChange: (html: string) => void;
  /** 画像ファイルをアップロードして表示 URL を返す（本文埋め込み用） */
  onImageUpload: (file: File) => Promise<string>;
  placeholder?: string;
}

/**
 * WordPress のクラシックエディタ風の簡易リッチテキストエディタ。
 *
 * - contentEditable + ツールバー（太字 / 見出し / 箇条書き / リンク / 画像）
 * - Enter で改行（ブロック）が入るので「入力に合わせて改行」できる
 * - 画像はツールバーからアップロードして本文に挿入
 * - 出力は HTML（既存の NewsManagementSection / NewsDetailPage と同じく
 *   dangerouslySetInnerHTML で描画される）
 */
const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange, onImageUpload, placeholder }) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  // 外部から value が変わったときだけ反映する。
  // 編集中（フォーカス時）に innerHTML を再代入するとカーソルが先頭へ飛ぶため、
  // 非フォーカス時かつ内容が異なる場合のみ同期する。
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (document.activeElement !== el && el.innerHTML !== (value || '')) {
      el.innerHTML = value || '';
    }
  }, [value]);

  const emitChange = () => {
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  };

  const runCommand = (command: string, arg?: string) => {
    editorRef.current?.focus();
    document.execCommand(command, false, arg);
    emitChange();
  };

  const insertHtml = (html: string) => {
    editorRef.current?.focus();
    document.execCommand('insertHTML', false, html);
    emitChange();
  };

  const handleLink = () => {
    const url = window.prompt('リンク先 URL を入力してください', 'https://');
    if (!url) return;
    editorRef.current?.focus();
    const selection = window.getSelection();
    if (selection && selection.toString().trim()) {
      document.execCommand('createLink', false, url);
    } else {
      insertHtml(
        `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color:#2563eb;text-decoration:underline;">${url}</a>`,
      );
      return;
    }
    emitChange();
  };

  const handleImageButton = () => fileRef.current?.click();

  const handleImageSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // 同じファイルを連続選択できるようにリセット
    if (!file) return;

    setUploading(true);
    try {
      const url = await onImageUpload(file);
      insertHtml(
        `<img src="${url}" alt="" style="max-width:100%;height:auto;border-radius:8px;margin:8px 0;" />`,
      );
    } catch {
      window.alert('画像のアップロードに失敗しました');
    } finally {
      setUploading(false);
    }
  };

  const toolbarButtonClass =
    'flex h-8 w-8 items-center justify-center rounded-tasky-xs text-gray-600 hover:bg-warm-hover hover:text-dark transition-colors disabled:opacity-50';

  return (
    <div className="rounded-tasky-sm border border-warm-border overflow-hidden">
      {/* エディタ内の表示用スタイル。Tailwind preflight で見出し/リストが
          潰れるため、エディタ内だけ見た目を復元して WYSIWYG にする。 */}
      <style>{`
        .news-rte[data-empty="true"]::before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
        .news-rte h2 { font-size: 1.25rem; font-weight: 700; margin: 0.6em 0 0.3em; }
        .news-rte ul { list-style: disc; padding-left: 1.5em; margin: 0.4em 0; }
        .news-rte ol { list-style: decimal; padding-left: 1.5em; margin: 0.4em 0; }
        .news-rte a { color: #2563eb; text-decoration: underline; }
        .news-rte img { max-width: 100%; height: auto; border-radius: 8px; margin: 8px 0; }
        .news-rte p { margin: 0.4em 0; }
      `}</style>

      {/* Toolbar */}
      <div className="flex items-center gap-0.5 border-b border-warm-border bg-warm-surface px-2 py-1.5">
        <button type="button" title="太字" className={toolbarButtonClass}
          onMouseDown={(e) => e.preventDefault()} onClick={() => runCommand('bold')}>
          <Bold size={16} />
        </button>
        <button type="button" title="見出し" className={toolbarButtonClass}
          onMouseDown={(e) => e.preventDefault()} onClick={() => runCommand('formatBlock', 'H2')}>
          <Heading2 size={16} />
        </button>
        <button type="button" title="箇条書き" className={toolbarButtonClass}
          onMouseDown={(e) => e.preventDefault()} onClick={() => runCommand('insertUnorderedList')}>
          <List size={16} />
        </button>
        <span className="mx-1 h-5 w-px bg-warm-border" />
        <button type="button" title="リンクを挿入" className={toolbarButtonClass}
          onMouseDown={(e) => e.preventDefault()} onClick={handleLink}>
          <LinkIcon size={16} />
        </button>
        <button type="button" title="画像を挿入" className={toolbarButtonClass} disabled={uploading}
          onMouseDown={(e) => e.preventDefault()} onClick={handleImageButton}>
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <ImageIcon size={16} />}
        </button>
      </div>

      {/* Editable area */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        data-placeholder={placeholder || '本文を入力してください'}
        data-empty={!value}
        onInput={(e) => {
          (e.currentTarget as HTMLDivElement).setAttribute(
            'data-empty',
            e.currentTarget.innerHTML === '' ? 'true' : 'false',
          );
          emitChange();
        }}
        className="news-rte min-h-[220px] max-h-[440px] overflow-y-auto px-3 py-2 text-sm leading-relaxed text-dark focus:outline-none"
        style={{ wordBreak: 'break-word' }}
      />

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleImageSelected}
      />
    </div>
  );
};

export default RichTextEditor;
